import { tr, uiLocale } from "../i18n/LanguageContext";
import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "../i18n/router";
import { Calendar, Clock, Users, Filter, Search, RotateCcw, Loader2, MapPin } from "lucide-react";
import EventImage from "../components/EventImage";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { practiceTypes } from "../data/mock";
import { useEventLanguages } from "../hooks/useEventLanguages";
import { formatEventPrice, getEffectivePricingType, PRICING_TYPES } from "../data/currencies";
import DateRangePicker from "../components/DateRangePicker";
import LocationFilter from "../components/LocationFilter";
import EventTypeBadge from "../components/EventTypeBadge";
import EventLocation from "../components/EventLocation";
import EventTypeToggles from "../components/EventTypeToggles";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EventsPage = () => {
  const { eventLanguages, getLanguageLabel } = useEventLanguages();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getInitialState = () => {
    const practiceParam = searchParams.get('practice') || searchParams.get('category') || "all";
    return {
      searchTerm: searchParams.get('search') || "",
      selectedLocation: { country: searchParams.get('country') || "Все страны", city: searchParams.get('city') || "Все города" },
      activeEventTypes: { online: searchParams.get('online') === 'true', offline: searchParams.get('offline') === 'true', hybrid: searchParams.get('hybrid') === 'true' },
      selectedPractice: practiceParam,
      selectedLanguage: searchParams.get('language') || "all",
      selectedDateRange: (() => {
        const from = searchParams.get('dateFrom');
        return from ? { from: new Date(from), to: searchParams.get('dateTo') ? new Date(searchParams.get('dateTo')) : null } : null;
      })()
    };
  };

  const initialState = getInitialState();
  const [searchTerm, setSearchTerm] = useState(initialState.searchTerm);
  const [selectedLocation, setSelectedLocation] = useState(initialState.selectedLocation);
  const [activeEventTypes, setActiveEventTypes] = useState(initialState.activeEventTypes);
  const [selectedPractice, setSelectedPractice] = useState(initialState.selectedPractice);
  const [selectedLanguage, setSelectedLanguage] = useState(initialState.selectedLanguage);
  const [selectedDateRange, setSelectedDateRange] = useState(initialState.selectedDateRange);
  const [resetTrigger, setResetTrigger] = useState(0);
  const eventsListHeadingRef = useRef(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/events`);
        if (!res.ok) throw new Error(tr("Ошибка загрузки событий"));
        setEvents(await res.json());
      } catch (err) { setError(err.message); }
      finally { setLoading(false); }
    })();
  }, []);

  const updateURLParams = useCallback((s) => {
    const p = new URLSearchParams();
    if (s.searchTerm) p.set('search', s.searchTerm);
    if (s.selectedLocation.country !== "Все страны") p.set('country', s.selectedLocation.country);
    if (s.selectedLocation.city !== "Все города") p.set('city', s.selectedLocation.city);
    if (s.activeEventTypes.online) p.set('online', 'true');
    if (s.activeEventTypes.offline) p.set('offline', 'true');
    if (s.activeEventTypes.hybrid) p.set('hybrid', 'true');
    if (s.selectedPractice !== "all") p.set('practice', s.selectedPractice);
    if (s.selectedLanguage !== "all") p.set('language', s.selectedLanguage);
    if (s.selectedDateRange?.from) { p.set('dateFrom', s.selectedDateRange.from.toISOString().split('T')[0]); if (s.selectedDateRange.to) p.set('dateTo', s.selectedDateRange.to.toISOString().split('T')[0]); }
    setSearchParams(p, { replace: true });
  }, [setSearchParams]);

  useEffect(() => { updateURLParams({ searchTerm, selectedLocation, activeEventTypes, selectedPractice, selectedLanguage, selectedDateRange }); }, [searchTerm, selectedLocation, activeEventTypes, selectedPractice, selectedLanguage, selectedDateRange, updateURLParams]);
  useEffect(() => { const c = searchParams.get('category'); if (c) { const p = new URLSearchParams(searchParams); p.delete('category'); p.set('practice', c); setSearchParams(p, { replace: true }); } }, []);

  const today = new Date().toISOString().split('T')[0];

  // Dynamic practice categories from actual events
  const dynamicPractices = useMemo(() => {
    const counts = {};
    events.forEach(ev => {
      if (ev.practice) counts[ev.practice] = (counts[ev.practice] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([value, count]) => {
        const label = tr(practiceTypes.find(p => p.value === value)?.label || value);
        return { value, label, count };
      });
  }, [events]);

  const filteredEvents = useMemo(() => {
    const organizerFilter = searchParams.get('organizer');
    return events.filter(event => {
      if (organizerFilter && event.organizerId !== organizerFilter) return false;
      const matchesSearch = event.title.toLowerCase().includes(searchTerm.toLowerCase()) || event.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesLocation = (() => {
        if (selectedLocation.city === "Онлайн") return event.city === "Онлайн";
        if (selectedLocation.country === "Все страны" && selectedLocation.city === "Все города") return true;
        if (selectedLocation.city !== "Все города") return event.city === selectedLocation.city;
        return true;
      })();
      const matchesType = (() => {
        const has = activeEventTypes.online || activeEventTypes.offline || activeEventTypes.hybrid;
        if (!has) return true;
        return (activeEventTypes.online && event.type === "online") || (activeEventTypes.offline && event.type === "offline") || (activeEventTypes.hybrid && event.type === "hybrid");
      })();
      const matchesPractice = selectedPractice === "all" || event.practice === selectedPractice;
      const matchesLanguage = selectedLanguage === "all" || (Array.isArray(event.languages) && event.languages.includes(selectedLanguage));
      const matchesDate = !selectedDateRange?.from || (() => {
        const d = new Date(event.date); const s = new Date(selectedDateRange.from); const e = selectedDateRange.to ? new Date(selectedDateRange.to) : new Date(selectedDateRange.from);
        d.setHours(0,0,0,0); s.setHours(0,0,0,0); e.setHours(23,59,59,999);
        return d >= s && d <= e;
      })();
      return matchesSearch && matchesLocation && matchesType && matchesPractice && matchesLanguage && matchesDate;
    });
  }, [events, searchTerm, selectedLocation, activeEventTypes, selectedPractice, selectedLanguage, selectedDateRange, searchParams]);

  // Split into upcoming and past, sorted
  const upcomingEvents = useMemo(() => filteredEvents.filter(e => e.date >= today).sort((a, b) => a.date.localeCompare(b.date)), [filteredEvents, today]);
  const pastEvents = useMemo(() => filteredEvents.filter(e => e.date < today).sort((a, b) => b.date.localeCompare(a.date)), [filteredEvents, today]);

  const getPracticeLabel = (v) => tr(practiceTypes.find(p => p.value === v)?.label || v);
  const handleDateRangeChange = useCallback((r) => setSelectedDateRange(r), []);
  const handleLocationChange = useCallback((l) => setSelectedLocation(l), []);
  const handleEventTypesChange = useCallback((t) => setActiveEventTypes(t), []);

  const resetAllFilters = () => {
    setSearchTerm(""); setSelectedLocation({ country: "Все страны", city: "Все города" });
    setActiveEventTypes({ online: false, offline: false, hybrid: false });
    setSelectedPractice("all"); setSelectedLanguage("all"); setSelectedDateRange(null); setResetTrigger(p => p + 1);
    setSearchParams({}, { replace: true });
  };

  const hasActiveFilters = useMemo(() => {
    return searchTerm !== "" || selectedLocation.country !== "Все страны" || selectedLocation.city !== "Все города" || activeEventTypes.online || activeEventTypes.offline || activeEventTypes.hybrid || selectedPractice !== "all" || selectedLanguage !== "all" || selectedDateRange !== null;
  }, [searchTerm, selectedLocation, activeEventTypes, selectedPractice, selectedLanguage, selectedDateRange]);

  const renderEventCard = (event, isPast) => (
    <div key={event.id} className={`eunoia-surface-soft overflow-hidden group hover:-translate-y-0.5 transition-all duration-300 ${isPast ? 'opacity-75' : ''}`} data-testid={`event-card-${event.id}`}>
      <div className="aspect-video overflow-hidden relative">
        <EventImage src={event.image} alt={event.title} hoverZoom />
        {isPast && (
          <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-medium bg-black/60 text-white backdrop-blur-sm" data-testid="past-badge">
            {tr("Прошедшее событие")}
          </div>
        )}
      </div>
      <div className="p-6">
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          {/* Render every practice tag on the card. Falls back to the legacy
              single `practice` field for events created before the multi-
              practice migration. */}
          {(() => {
            const list = (Array.isArray(event.practices) && event.practices.length)
              ? event.practices
              : (event.practice ? [event.practice] : []);
            return list.map((slug) => (
              <span
                key={slug}
                className="px-3 py-1 text-xs font-medium rounded-full"
                style={{ backgroundColor: 'var(--eunoia-accent-whisper)', color: 'var(--eunoia-accent)' }}
                data-testid={`event-card-practice-${event.id}-${slug}`}
              >
                {getPracticeLabel(slug)}
              </span>
            ));
          })()}
          {(Array.isArray(event.languages) ? event.languages : []).map((code) => (
            <span
              key={`lang-${code}`}
              className="px-3 py-1 text-xs font-medium rounded-full"
              style={{ backgroundColor: 'var(--eunoia-accent-whisper)', color: 'var(--eunoia-accent)' }}
              data-testid={`event-card-language-${event.id}-${code}`}
            >
              {getLanguageLabel(code)}
            </span>
          ))}
          <EventTypeBadge type={event.type} />
          {(() => {
            const type = getEffectivePricingType(event);
            const label = formatEventPrice(event);
            if (type === PRICING_TYPES.PAID) {
              return (
                <span
                  className="px-3 py-1 text-xs font-semibold rounded-full"
                  style={{ backgroundColor: 'var(--eunoia-deep)', color: 'white' }}
                  data-testid={`price-badge-${event.id}`}
                >
                  {label}
                </span>
              );
            }
            if (type === PRICING_TYPES.DONATION) {
              return (
                <span
                  className="px-3 py-1 text-xs font-medium rounded-full"
                  style={{ backgroundColor: '#FBE4E4', color: '#B23A48' }}
                  data-testid={`donation-badge-${event.id}`}
                >
                  {label}
                </span>
              );
            }
            return (
              <span
                className="px-3 py-1 text-xs font-medium rounded-full"
                style={{ backgroundColor: '#E8F0E5', color: '#3F6B45' }}
                data-testid={`free-badge-${event.id}`}
              >
                {label}
              </span>
            );
          })()}
        </div>
        <h3 className="eunoia-title-small mb-3" style={{ color: 'var(--eunoia-deep)', fontWeight: '500' }}>{event.title}</h3>
        <p className="eunoia-body mb-4 line-clamp-2">{event.description}</p>
        <div className="space-y-3 mb-6">
          <div className="flex items-center gap-2 eunoia-caption"><Calendar className="h-4 w-4" style={{ color: 'var(--eunoia-accent)' }} /><span>{new Date(event.date).toLocaleDateString(uiLocale())}</span></div>
          <div className="flex items-center gap-2 eunoia-caption"><Clock className="h-4 w-4" style={{ color: 'var(--eunoia-accent)' }} /><span>{event.time}</span></div>
          <EventLocation event={event} showDetails={false} />
          <div className="flex items-center gap-2 eunoia-caption"><Users className="h-4 w-4" style={{ color: 'var(--eunoia-accent)' }} /><span data-testid={`card-organizers-${event.id}`}>{[event.organizer, ...(Array.isArray(event.coOrganizers) ? event.coOrganizers.map(c => c.displayName) : [])].filter(Boolean).join(', ')}</span></div>
        </div>
        {isPast ? (
          <button disabled className="w-full px-6 py-3 rounded-xl text-sm font-medium bg-gray-100 text-gray-400 cursor-not-allowed" data-testid={`past-btn-${event.id}`}>
            {tr("Событие завершено")}
          </button>
        ) : (
          <button onClick={() => navigate(`/events/${event.id}`)} className="eunoia-btn-primary w-full" data-testid={`participate-btn-${event.id}`}>
            {tr("Участвовать")}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen eunoia-space-lg">
      <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header */}
        <div className="text-center eunoia-space-xl">
          <h1 className="eunoia-title-large mb-6">{tr("Календарь событий")}</h1>
          <p className="eunoia-body-large max-w-2xl mx-auto">{tr("Найдите духовные практики и события, которые резонируют с вашей душой")}</p>
        </div>

        {/* Filters */}
        <div className="eunoia-surface p-8 eunoia-space-md">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Filter className="h-5 w-5" style={{ color: 'var(--eunoia-accent)' }} />
              <h2 className="eunoia-title-small">{tr("Фильтры")}</h2>
            </div>
            {hasActiveFilters && (
              <button onClick={resetAllFilters} className="eunoia-btn-secondary text-sm flex items-center gap-2"><RotateCcw className="h-4 w-4" />{tr("Сбросить")}</button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="lg:col-span-2">
              <div className="relative">
                <input placeholder={tr("Поиск событий...")} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="eunoia-input pr-14" />
                <Search className="absolute right-6 top-1/2 transform -translate-y-1/2 h-4 w-4" style={{ color: 'var(--eunoia-soft)' }} />
              </div>
            </div>
            <LocationFilter onLocationChange={handleLocationChange} resetTrigger={resetTrigger} initialCountry={initialState.selectedLocation.country} initialCity={initialState.selectedLocation.city} isOnlineActive={activeEventTypes.online} />

            {/* Dynamic practice filter */}
            <Select value={selectedPractice} onValueChange={setSelectedPractice}>
              <SelectTrigger className="eunoia-input" data-testid="practice-filter">
                <SelectValue placeholder={tr("Практика")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{tr("Все практики")}</SelectItem>
                {dynamicPractices.map(p => (
                  <SelectItem key={p.value} value={p.value} data-testid={`filter-practice-${p.value}`}>
                    {tr(p.label)} ({p.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
              <SelectTrigger className="eunoia-input" data-testid="language-filter">
                <SelectValue placeholder={tr("Язык")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="filter-language-all">{tr("Все языки")}</SelectItem>
                {eventLanguages.map(l => (
                  <SelectItem key={l.value} value={l.value} data-testid={`filter-language-${l.value}`}>{tr(l.label)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="mt-4">
            <EventTypeToggles activeTypes={activeEventTypes} onTypesChange={handleEventTypesChange} resetTrigger={resetTrigger} initialTypes={initialState.activeEventTypes} />
          </div>
          <div className="mt-4">
            <DateRangePicker onDateRangeChange={handleDateRangeChange} resetTrigger={resetTrigger} initialRange={initialState.selectedDateRange} className="w-full max-w-xs" />
          </div>
        </div>

        {/* Results count */}
        <div className="eunoia-space-sm" ref={eventsListHeadingRef}>
          <p className="eunoia-body">
            {tr("Найдено:")} <span className="font-medium" style={{ color: 'var(--eunoia-accent)' }}>{upcomingEvents.length} {tr("предстоящих")}</span>
            {pastEvents.length > 0 && <span className="ml-2" style={{ color: 'var(--eunoia-soft)' }}>+ {pastEvents.length} {tr("прошедших")}</span>}
          </p>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin" style={{ color: 'var(--eunoia-accent)' }} /><span className="ml-3 eunoia-body">{tr("Загрузка...")}</span></div>
        )}
        {error && !loading && (
          <div className="text-center py-16">
            <Calendar className="h-12 w-12 mx-auto mb-4" style={{ color: 'var(--eunoia-accent)' }} />
            <p className="eunoia-body mb-4">{error}</p>
            <button onClick={() => window.location.reload()} className="eunoia-btn-secondary">{tr("Попробовать снова")}</button>
          </div>
        )}

        {/* Upcoming Events */}
        {!loading && !error && upcomingEvents.length > 0 && (
          <div className="mb-12">
            <h2 className="eunoia-title-small mb-6 flex items-center gap-2">
              <Calendar className="h-5 w-5" style={{ color: 'var(--eunoia-accent)' }} /> {tr("Предстоящие события")}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {upcomingEvents.map(e => renderEventCard(e, false))}
            </div>
          </div>
        )}

        {/* Past Events */}
        {!loading && !error && pastEvents.length > 0 && (
          <div className="mb-12">
            <h2 className="eunoia-title-small mb-6 flex items-center gap-2" style={{ color: 'var(--eunoia-soft)' }}>
              <Clock className="h-5 w-5" /> {tr("Прошедшие события")}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pastEvents.map(e => renderEventCard(e, true))}
            </div>
          </div>
        )}

        {/* Empty states */}
        {!loading && !error && events.length === 0 && (
          <div className="text-center eunoia-space-xl">
            <Calendar className="h-12 w-12 mx-auto mb-6" style={{ color: 'var(--eunoia-accent)' }} />
            <h3 className="eunoia-title-medium mb-4">{tr("Пока нет событий")}</h3>
            <p className="eunoia-body max-w-md mx-auto mb-6">{tr("Организаторы смогут публиковать события после одобрения заявки")}</p>
          </div>
        )}
        {!loading && !error && events.length > 0 && filteredEvents.length === 0 && (
          <div className="text-center eunoia-space-xl">
            <Calendar className="h-12 w-12 mx-auto mb-6" style={{ color: 'var(--eunoia-accent)' }} />
            <h3 className="eunoia-title-medium mb-4">{tr("События не найдены")}</h3>
            <p className="eunoia-body max-w-md mx-auto">{tr("Попробуйте изменить параметры фильтра")}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsPage;
